'use client';

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { withDisplayName } from '../../lib/display-name';
import {
  SSPLITTER_DEFAULT_LIMITS,
  SSPLITTER_DEFAULT_VALUE,
  SSPLITTER_KEY_STEP,
  type SSplitterUnit,
} from './splitter.config';

/** 구분선 두께 · 선 주변 패딩 — 토큰이 소유한다. */
const LINE = 'var(--cmp-splitter-border-width)';
const PAD = 'var(--cmp-splitter-border-padding)';
/** 히트 영역은 구분선 좌우로 패딩만큼 넓힌 것이다. absolute 라 레이아웃을 밀지 않는다. */
const HIT = `calc(${LINE} + ${PAD} * 2)`;
const HIT_END = `calc(0px - ${PAD})`;

interface SSplitterContextValue {
  /** true면 패널을 위아래로 쌓는다 (구분선은 가로) */
  vertical: boolean;
  /** 첫 패널이 받을 CSS 크기 ('30%' · '240px') */
  sizeCss: string;
  /** 두 패널 사이에 놓이는 구분선. SSplitter.Before 가 자기 뒤에 렌더한다 */
  divider: ReactNode;
}

const SSplitterContext = createContext<SSplitterContextValue | null>(null);

function useSSplitterContext(who: string): SSplitterContextValue {
  const ctx = useContext(SSplitterContext);
  if (!ctx) throw new Error(`${who} 은 <SSplitter> 안에서만 사용할 수 있습니다.`);
  return ctx;
}

/** 패널은 순수 슬롯이다 — 크기·제약은 전부 루트가 소유한다. */
export type SSplitterPaneProps = HTMLAttributes<HTMLDivElement>;

export interface SSplitterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue'> {
  /** 첫 패널 크기. unit 단위. 주면 controlled */
  value?: number;
  /** 첫 패널 초기 크기. uncontrolled. 기본은 '%' 면 50, 'px' 면 240 */
  defaultValue?: number;
  /** 크기가 확정될 때. emitImmediately 가 아니면 드래그를 놓는 순간 한 번만 온다 */
  onValueChange?: (value: number) => void;
  /** 모델·limits 를 읽는 단위 */
  unit?: SSplitterUnit;
  /** [최소, 최대]. 생략하면 '%' 는 [10, 90], 'px' 는 [50, Infinity] */
  limits?: readonly [number, number];
  /** 드래그하는 동안에도 onValueChange 를 계속 보낸다 */
  emitImmediately?: boolean;
  /** true면 패널을 위아래로 쌓는다 (Quasar q-splitter 의 horizontal 에 해당) */
  vertical?: boolean;
  /** true면 크기를 바꿀 수 없다. 커서도 구분선도 나오지 않는다 */
  disabled?: boolean;
  /** 구분선에 얹을 클래스 */
  dividerClassName?: string;
  /** 구분선에 얹을 인라인 스타일 */
  dividerStyle?: CSSProperties;
  /** SSplitter.Before 와 SSplitter.After 둘 */
  children?: ReactNode;
}

/**
 * SSplitter — Quasar `q-splitter` 포팅. 두 패널의 경계를 끌어 크기를 바꾼다.
 *
 * 구분선은 평소 자리만 잡고(두께 `--cmp-splitter-border-width`) 칠해지지 않다가,
 * hover·포커스·드래그일 때 `--cmp-splitter-border-color` 로 드러난다 — 조절할 수 있는 자리라는 신호다.
 *
 * 자식은 `SSplitter.Before` 와 `SSplitter.After` 다. 둘 다 루트의 직접 자식이어야 한다.
 * 각 패널은 넘치는 만큼 스스로 스크롤한다(Quasar 와 같음).
 *
 * `emitImmediately` 가 아니면 드래그 중에는 내부에서만 움직이고, 놓는 순간 한 번 통지한다.
 *
 * 키보드: 경계에 포커스를 준 뒤 ←→(상/하는 ↑↓), Shift 조합이면 크게, Home·End 로 최소·최대.
 *
 * @example
 * <SSplitter defaultValue={30} limits={[20, 60]}>
 *   <SSplitter.Before>내비게이션</SSplitter.Before>
 *   <SSplitter.After>본문</SSplitter.After>
 * </SSplitter>
 */
const SSplitterRoot = forwardRef<HTMLDivElement, SSplitterProps>(function SSplitter(
  {
    value,
    defaultValue,
    onValueChange,
    unit = '%',
    limits,
    emitImmediately = false,
    vertical = false,
    disabled = false,
    dividerClassName,
    dividerStyle,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  const [uncontrolledValue, setUncontrolledValue] = useState(
    () => defaultValue ?? SSPLITTER_DEFAULT_VALUE[unit],
  );
  const isControlled = value !== undefined;
  const committedValue = isControlled ? value : uncontrolledValue;
  /**
   * 드래그 중 미리보기. emitImmediately 가 아니면 놓기 전까지 밖에 알리지 않으므로,
   * 화면에 보일 값을 여기에 따로 들고 있어야 한다.
   */
  const [dragValue, setDragValue] = useState<number | null>(null);
  const currentValue = dragValue ?? committedValue;

  const [dragging, setDragging] = useState(false);

  const [minLimit, maxLimit] = limits ?? SSPLITTER_DEFAULT_LIMITS[unit];

  // 드래그 중 리스너가 리렌더 사이에도 최신 값을 동기적으로 읽어야 한다.
  const stateRef = useRef({ committedValue, unit, vertical, minLimit, maxLimit });
  stateRef.current = { committedValue, unit, vertical, minLimit, maxLimit };
  const emitRef = useRef({ onValueChange, emitImmediately, isControlled });
  emitRef.current = { onValueChange, emitImmediately, isControlled };

  /** 컨테이너 크기를 알아야 상한이 정해진다(px 단위는 컨테이너를 넘을 수 없다). */
  const measure = useCallback(() => {
    const el = rootRef.current;
    if (!el) return 0;
    return stateRef.current.vertical ? el.clientHeight : el.clientWidth;
  }, []);

  /** Quasar 와 같은 순서로 자른다 — 단위 상한 → limits 상한 → limits 하한. */
  const clamp = useCallback((next: number, containerSize: number) => {
    const { unit: u, minLimit: lo, maxLimit: hi } = stateRef.current;
    const unitMax = u === '%' ? 100 : containerSize;
    return Math.min(unitMax, hi, Math.max(lo, next));
  }, []);

  const commit = useCallback((next: number) => {
    if (next === stateRef.current.committedValue) return;
    // 다음 렌더 전까지의 기준. 한 프레임에 mousemove 가 여러 번 들어와도 같은 값을 두 번 알리지 않는다.
    stateRef.current.committedValue = next;
    if (!emitRef.current.isControlled) setUncontrolledValue(next);
    emitRef.current.onValueChange?.(next);
  }, []);

  // 드래그 도중 언마운트되면 document 리스너와 body 스타일이 남는다.
  const endDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endDragRef.current?.(), []);

  const startResize = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (disabled || typeof document === 'undefined') return;
      // preventDefault 로 텍스트 선택을 막으면 포커스도 따라오지 않는다.
      // 드래그를 놓은 자리에서 바로 방향키로 이어갈 수 있도록 직접 옮겨 준다.
      event.preventDefault();
      event.currentTarget.focus();

      const isVertical = stateRef.current.vertical;
      const containerSize = measure();
      const startPos = isVertical ? event.clientY : event.clientX;
      const startValue = clamp(stateRef.current.committedValue, containerSize);
      // px 는 이동 거리가 곧 값이고, % 는 컨테이너 대비 비율로 환산한다.
      const multiplier =
        stateRef.current.unit === '%' ? (containerSize === 0 ? 0 : 100 / containerSize) : 1;
      let latest = startValue;

      const { body } = document;
      const prevUserSelect = body.style.userSelect;
      const prevCursor = body.style.cursor;
      // 패널 텍스트가 드래그로 선택되지 않게, 커서가 패널 위에서 바뀌지 않게 잠근다.
      body.style.userSelect = 'none';
      body.style.cursor = isVertical ? 'row-resize' : 'col-resize';

      const onMove = (moveEvent: MouseEvent) => {
        const delta = (isVertical ? moveEvent.clientY : moveEvent.clientX) - startPos;
        latest = clamp(startValue + multiplier * delta, containerSize);
        setDragValue(latest);
        if (emitRef.current.emitImmediately) commit(latest);
      };
      const endDrag = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', endDrag);
        body.style.userSelect = prevUserSelect;
        body.style.cursor = prevCursor;
        endDragRef.current = null;
        setDragging(false);
        setDragValue(null);
        commit(latest);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', endDrag);
      endDragRef.current = endDrag;
      setDragging(true);
    },
    [disabled, measure, clamp, commit],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      const { unit: u, vertical: isVertical, minLimit: lo } = stateRef.current;
      const step = SSPLITTER_KEY_STEP[u][event.shiftKey ? 'large' : 'normal'];
      const decrease = isVertical ? 'ArrowUp' : 'ArrowLeft';
      const increase = isVertical ? 'ArrowDown' : 'ArrowRight';

      let next: number | null = null;
      if (event.key === decrease) next = stateRef.current.committedValue - step;
      else if (event.key === increase) next = stateRef.current.committedValue + step;
      else if (event.key === 'Home') next = lo;
      // 상한은 clamp 가 안다 — 단위·컨테이너·limits 중 가장 작은 값이다.
      else if (event.key === 'End') next = Number.MAX_SAFE_INTEGER;
      if (next === null) return;

      event.preventDefault();
      commit(clamp(next, measure()));
    },
    [disabled, measure, clamp, commit],
  );

  const sizeCss = unit === '%' ? `${currentValue}%` : `${Math.round(currentValue)}px`;

  const divider = (
    <div className="relative z-[1] shrink-0" style={{ flexBasis: LINE }}>
      {/* 포커스 가능한 separator = ARIA window splitter 로, 값을 가지는 위젯이다.
          jsx-a11y 는 separator 를 항상 비대화형으로 보므로 여기서만 끈다. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="separator"
        aria-orientation={vertical ? 'horizontal' : 'vertical'}
        aria-valuenow={Math.round(currentValue)}
        aria-valuemin={Math.round(minLimit)}
        aria-valuemax={Number.isFinite(maxLimit) ? Math.round(maxLimit) : undefined}
        aria-valuetext={sizeCss}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onMouseDown={startResize}
        onKeyDown={handleKeyDown}
        className={cn(
          'group absolute flex outline-none',
          vertical ? 'inset-x-0 flex-col justify-center' : 'inset-y-0 justify-center',
          disabled ? 'cursor-default' : vertical ? 'cursor-row-resize' : 'cursor-col-resize',
          dividerClassName,
        )}
        style={{
          ...(vertical ? { bottom: HIT_END, height: HIT } : { right: HIT_END, width: HIT }),
          ...dividerStyle,
        }}
      >
        <span
          aria-hidden
          className={cn(
            // 평상시엔 자리만 잡고 칠하지 않는다 — 색이 붙는 것 자체가 조절 가능하다는 신호다.
            'transition-colors',
            vertical ? 'h-(--cmp-splitter-border-width)' : 'w-(--cmp-splitter-border-width)',
            !disabled &&
              'group-hover:bg-(--cmp-splitter-border-color) group-focus-visible:bg-(--cmp-splitter-border-color)',
            !disabled && dragging && 'bg-(--cmp-splitter-border-color)',
          )}
        />
      </div>
    </div>
  );

  /**
   * 일부러 memo 하지 않는다 — 패널은 소비자가 만든 element 라 루트가 리렌더돼도 그대로다.
   * context 가 매번 새 객체여야 드래그 중 크기와 구분선이 패널 쪽에 전달된다.
   */
  const ctx: SSplitterContextValue = { vertical, sizeCss, divider };

  return (
    <SSplitterContext.Provider value={ctx}>
      <div
        ref={setRootRef}
        className={cn('flex size-full', vertical ? 'flex-col' : 'flex-row', className)}
        style={style}
        {...rest}
      >
        {children}
      </div>
    </SSplitterContext.Provider>
  );
});

/** 첫 패널이 크기를 받고, 둘째 패널은 남는 자리를 모두 가져간다. */
function usePane(pane: 'before' | 'after', className?: string, style?: CSSProperties) {
  const ctx = useSSplitterContext(`SSplitter.${pane === 'before' ? 'Before' : 'After'}`);
  const isSized = pane === 'before';
  return {
    divider: ctx.divider,
    className: cn(
      'min-h-0 min-w-0 overflow-auto',
      isSized ? 'shrink-0' : 'flex-1 basis-0',
      className,
    ),
    style: isSized ? { [ctx.vertical ? 'height' : 'width']: ctx.sizeCss, ...style } : style,
  };
}

const SSplitterBeforeImpl = forwardRef<HTMLDivElement, SSplitterPaneProps>(function SSplitterBefore(
  { className, style, children, ...rest },
  ref,
) {
  const pane = usePane('before', className, style);
  return (
    <>
      <div ref={ref} className={pane.className} style={pane.style} {...rest}>
        {children}
      </div>
      {pane.divider}
    </>
  );
});

const SSplitterAfterImpl = forwardRef<HTMLDivElement, SSplitterPaneProps>(function SSplitterAfter(
  { className, style, children, ...rest },
  ref,
) {
  const pane = usePane('after', className, style);
  return (
    <div ref={ref} className={pane.className} style={pane.style} {...rest}>
      {children}
    </div>
  );
});

const SSplitterBefore = withDisplayName(SSplitterBeforeImpl, 'SSplitter.Before');
const SSplitterAfter = withDisplayName(SSplitterAfterImpl, 'SSplitter.After');

// Object.assign 은 첫 인자를 변형하므로 번들러가 지우지 못한다. 여기서는 대상이
// 이 모듈에서 방금 만든 값뿐이라 안전하게 pure 로 표시할 수 있다.
export const SSplitter = /* @__PURE__ */ Object.assign(SSplitterRoot, {
  Before: SSplitterBefore,
  After: SSplitterAfter,
});
